<script setup>
import axios from 'axios';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { extractApiError } from '../apiError';
import {
  formatCompactMoney, formatCurrencySums, formatMoney, formatNumber, formatSignedMoney, formatSignedPercent, signClass, toNumber,
} from '../format';
import { dayChange } from '../heatmap';
import { intlLocale, locale, t } from '../i18n';
import {
  currentPriceOf, marketValueOf, yieldAbsOf, yieldPercentOf,
} from '../positionView';
import { portfolios } from '../portfolioStore';
import { readUiState, writeUiState } from '../uiState';
import { PANEL, chipClass } from '../ui';
import {
  CATEGORIES, dayLabel, groupByDay, plural,
} from '../operations';
import InstrumentChart from './InstrumentChart.vue';
import InstrumentHoldings from './InstrumentHoldings.vue';
import InstrumentPayoutCalc from './InstrumentPayoutCalc.vue';
import NewsPostList from './NewsPostList.vue';
import OperationRow from './OperationRow.vue';

// The instrument screen (docs/instrument/SPEC.md): live price and day change, «У вас» — one block per account
// that holds it (decision 7), tabs «График | Новости». Opened from the portfolio, the heatmap and the quotes.
const props = defineProps({
  uid: {
    type: String,
    required: true,
  },
  livePrices: {
    type: Object,
    default: () => ({}),
  },
  desktop: {
    type: Boolean,
    default: false,
  },
  // Bumped by pull to refresh
  dataVersion: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['title']);

const POLL_MS = 5000;
const PERIODS = ['1d', '1w', '1m', '1y', '5y', 'all'];
const SUFFIX = { RUB: ' ₽', USD: ' $' };
const MOSCOW_DATE = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Moscow' });

const card = ref(null);
const loading = ref(true);
const error = ref('');
let pollTimer = null;

async function loadCard() {
  try {
    const { data } = await axios.get(`/instruments/${props.uid}`);
    card.value = data;
    error.value = '';
  } catch (e) {
    if (e?.response?.status !== 401) {
      error.value = e?.response?.status === 404 ? t('instrument.notFound') : extractApiError(e, t('instrument.loadFailed'));
    }
  } finally {
    loading.value = false;
  }
}

const name = computed(() => {
  if (!card.value) {
    return '';
  }
  return card.value.quote ? t(`quotes.name.${card.value.quote}`) : (card.value.name || card.value.ticker || '');
});
const title = computed(() => (card.value?.quote ? name.value : (card.value?.ticker || name.value)));
watch(title, (value) => emit('title', value), { immediate: true });

const subtitle = computed(() => {
  const c = card.value;
  if (!c || c.quote) {
    return '';
  }
  const industry = c.industry ? (locale.value === 'en' ? c.industry.name_en : c.industry.name_ru) : null;
  return [c.name ? c.ticker : null, industry].filter(Boolean).join(' · ');
});

// Quotes keep their own digits and suffix (docs/quotes/SPEC.md); others are money in their currency.
function formatPrice(value) {
  const c = card.value;
  const n = toNumber(value);
  if (!c || n === null) {
    return '—';
  }
  if (c.quote) {
    const text = new Intl.NumberFormat(intlLocale(), { minimumFractionDigits: c.decimals, maximumFractionDigits: c.decimals }).format(n);
    return text + (SUFFIX[c.currency] ?? '');
  }
  return c.currency ? formatMoney(n, c.currency) : formatNumber(n);
}

const price = computed(() => toNumber(card.value?.price));
const change = computed(() => dayChange(card.value?.price, card.value?.prev_close));
const changeAbs = computed(() => {
  const prev = toNumber(card.value?.prev_close);
  return price.value === null || prev === null ? null : price.value - prev;
});
const changeText = computed(() => {
  if (change.value === null) {
    return '';
  }
  const sign = changeAbs.value > 0 ? '+' : changeAbs.value < 0 ? '−' : '';
  const abs = card.value.quote || !card.value.currency
    ? sign + formatPrice(Math.abs(changeAbs.value))
    : formatSignedMoney(changeAbs.value, card.value.currency);
  return `${abs} · ${formatSignedPercent(change.value, 2)}`;
});

// «У вас»: every account holding the instrument, each on its own (decision 7).
const holdings = computed(() => portfolios.value.flatMap((account) => {
  const p = (account.positions ?? []).find((x) => x.instrument_uid === props.uid);
  if (!p) {
    return [];
  }
  return [{
    key: account.id,
    account: account.name || account.account_id,
    position: p,
    value: marketValueOf(p, props.livePrices),
    current: currentPriceOf(p, props.livePrices),
    yieldAbs: yieldAbsOf(p, props.livePrices),
    yieldPercent: yieldPercentOf(p, props.livePrices),
  }];
}));

// Market facts of index shares (former heatmap sheet, decision: moved here).
const facts = computed(() => {
  const c = card.value;
  if (!c) {
    return [];
  }
  // Morning before the first trades: the latest day's turnover, with its date (docs/heatmap/SPEC.md, decision 12).
  const today = MOSCOW_DATE.format(new Date());
  const turnoverLabel = c.turnover_date && c.turnover_date !== today
    ? t('heatmap.turnoverOn', { date: new Date(`${c.turnover_date}T12:00:00Z`).toLocaleDateString(intlLocale(), { day: '2-digit', month: '2-digit' }) })
    : t('heatmap.turnover');
  return [
    c.turnover !== null ? { key: 'turnover', label: turnoverLabel, value: formatCompactMoney(c.turnover) } : null,
    c.capitalization !== null ? { key: 'cap', label: t('heatmap.capitalization'), value: formatCompactMoney(c.capitalization) } : null,
    c.index_weight !== null ? { key: 'weight', label: t('heatmap.indexWeight'), value: `${formatNumber(c.index_weight, 2)} %` } : null,
  ].filter(Boolean);
});

// The screen always opens on the chart (decision 16); the period is remembered on the device.
// «Дивиденды» — shares and funds (decision 9), «Операции» — anything but indexes and commodities.
const tabs = computed(() => [
  'chart',
  // «Расчёт» — the TPAY payout calculation, right after the chart (docs/tpay/SPEC.md, decisions 2, 5).
  ...(card.value?.has_payout_calc ? ['calc'] : []),
  ...(card.value?.has_dividends ? ['dividends'] : []),
  ...(card.value && card.value.type !== 'indicative' ? ['operations'] : []),
  'news',
]);
const tab = ref('chart');
const shownTab = computed(() => (tabs.value.includes(tab.value) ? tab.value : 'chart'));
const period = ref(PERIODS.includes(readUiState('instrument.period', '1d')) ? readUiState('instrument.period', '1d') : '1d');
watch(period, (value) => {
  writeUiState('instrument.period', value);
  loadCandles();
});

const candles = ref([]);
const candlesLoading = ref(true);
const candlesError = ref('');
let candleRequest = 0;

async function loadCandles() {
  const id = ++candleRequest;
  candlesLoading.value = true;
  candlesError.value = '';
  try {
    const { data } = await axios.get(`/instruments/${props.uid}/candles`, { params: { period: period.value } });
    if (id === candleRequest) {
      candles.value = data.points.map(([time, value]) => [time, Number(value)]);
    }
  } catch (e) {
    if (id === candleRequest && e?.response?.status !== 401) {
      candlesError.value = extractApiError(e, t('instrument.chartFailed'));
    }
  } finally {
    if (id === candleRequest) {
      candlesLoading.value = false;
    }
  }
}

// The live price is the last point when it is newer than the last candle.
const points = computed(() => {
  const list = candles.value;
  const time = card.value?.price_time;
  if (price.value === null || !time || list.length === 0 || Date.parse(time) <= Date.parse(list[list.length - 1][0])) {
    return list;
  }
  return [...list, [time, price.value]];
});

// Change over the period: the day from the base close, longer ones from the first point.
const periodChange = computed(() => {
  const list = points.value;
  if (list.length === 0) {
    return null;
  }
  const last = list[list.length - 1][1];
  const base = period.value === '1d' ? (toNumber(card.value?.prev_close) ?? list[0][1]) : list[0][1];
  return base ? (last / base - 1) * 100 : null;
});

// News tab: posts with the instrument's hashtags, paged like the news screen.
const news = ref([]);
const newsCursor = ref(null);
const newsLoading = ref(false);
const newsLoaded = ref(false);
const newsError = ref('');

async function loadNews(more = false) {
  if (newsLoading.value || (more && !newsCursor.value)) {
    return;
  }
  newsLoading.value = true;
  newsError.value = '';
  try {
    const { data } = await axios.get(`/instruments/${props.uid}/news`, { params: more ? { cursor: newsCursor.value } : {} });
    news.value = more ? [...news.value, ...data.items] : data.items;
    newsCursor.value = data.next_cursor;
    newsLoaded.value = true;
  } catch (e) {
    if (e?.response?.status !== 401) {
      newsError.value = extractApiError(e, t('instrument.newsFailed'));
    }
  } finally {
    newsLoading.value = false;
  }
}

watch(shownTab, (value) => {
  if (value === 'news' && !newsLoaded.value) {
    loadNews();
  }
}, { immediate: true });

// Dividends: the tab and the «D» marks of paid ones on the chart (decisions 8–10).
const dividends = ref([]);
const dividendsLoaded = ref(false);
const dividendsError = ref('');

async function loadDividends() {
  dividendsError.value = '';
  try {
    const { data } = await axios.get(`/instruments/${props.uid}/dividends`);
    dividends.value = data.items;
    dividendsLoaded.value = true;
  } catch (e) {
    if (e?.response?.status !== 401) {
      dividendsError.value = extractApiError(e, t('instrument.dividendsFailed'));
    }
  }
}

watch(() => card.value?.has_dividends, (has) => {
  if (has && !dividendsLoaded.value) {
    loadDividends();
  }
});

function shortDay(date) {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString(intlLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' });
}

const markers = computed(() => dividends.value
  .filter((d) => (d.status === 'paid' || d.status === 'awaiting') && d.amount)
  .map((d) => ({ date: d.record_date, text: t('instrument.dividendMark', { amount: formatMoney(d.amount, d.currency), date: shortDay(d.record_date) }) })));

// «Показатели» of a share (decision 13): asked once, rows without data are left out.
const fundamentals = ref(null);

async function loadFundamentals() {
  try {
    const { data } = await axios.get(`/instruments/${props.uid}/fundamentals`);
    fundamentals.value = data.values;
  } catch {
    // The block is extra: without it the screen is complete.
    fundamentals.value = null;
  }
}

watch(() => card.value?.type, (type) => {
  if (type === 'share' && !card.value.quote && fundamentals.value === null) {
    loadFundamentals();
  }
});

const compactNumber = (value) => new Intl.NumberFormat(intlLocale(), { notation: 'compact', maximumFractionDigits: 1 }).format(value);

const fundamentalGroups = computed(() => {
  const f = fundamentals.value;
  if (!f) {
    return [];
  }
  const currency = f.currency || 'RUB';
  const ratio = (v) => formatNumber(v, 2);
  const percent = (v) => `${formatNumber(v, 2)} %`;
  const money = (v) => formatCompactMoney(v, currency);
  const groups = [
    ['valuation', [['pe', 'P/E', ratio], ['pb', 'P/B', ratio], ['ps', 'P/S', ratio], ['ev_ebitda', 'EV/EBITDA', ratio]]],
    ['debt', [['debt', t('instrument.f.debt'), money], ['net_debt_ebitda', t('instrument.f.netDebtEbitda'), ratio], ['debt_equity', t('instrument.f.debtEquity'), percent]]],
    ['profitability', [['roe', 'ROE', percent], ['roa', 'ROA', percent], ['net_margin', t('instrument.f.netMargin'), percent]]],
    ['financials', [
      ['revenue', t('instrument.f.revenue'), money], ['ebitda', 'EBITDA', money], ['net_income', t('instrument.f.netIncome'), money],
      ['eps', 'EPS', (v) => formatMoney(v, currency)], ['fcf', t('instrument.f.fcf'), money],
    ]],
    ['dividends', [['dividend_yield', t('instrument.f.dividendYield'), percent], ['payout', t('instrument.f.payout'), percent]]],
    ['other', [
      ['beta', t('instrument.f.beta'), ratio], ['free_float', 'Free float', (v) => `${formatNumber(v * 100, 1)} %`],
      ['range_52w', t('instrument.f.range52w'), null], ['shares', t('instrument.f.shares'), compactNumber],
    ]],
  ];
  return groups.map(([key, rows]) => ({
    key,
    title: t(`instrument.fGroup.${key}`),
    rows: rows.flatMap(([field, label, format]) => {
      if (field === 'range_52w') {
        return f.low_52w !== null && f.high_52w !== null
          ? [{ key: field, label, value: `${formatMoney(f.low_52w, currency)} — ${formatMoney(f.high_52w, currency)}`, negative: false }]
          : [];
      }
      const value = f[field];
      return value === null || value === undefined ? [] : [{ key: field, label, value: format(value), negative: value < 0 }];
    }),
  })).filter((group) => group.rows.length > 0);
});

// Future approved and recommended — green, forecasts — violet, past — plain (decision 9).
const STATUS_CLASS = {
  approved: 'bg-emerald-500/15 text-emerald-300',
  recommended: 'bg-emerald-500/15 text-emerald-300',
  forecast: 'bg-violet-500/15 text-violet-300',
  awaiting: 'bg-slate-800 text-slate-300',
  paid: 'bg-slate-800 text-slate-400',
};
const AMOUNT_CLASS = { approved: 'text-emerald-300', recommended: 'text-emerald-300', forecast: 'text-violet-300' };

const dividendRows = computed(() => dividends.value.map((d, index) => {
  // Forecasts have no yield from the API: estimated from today's price.
  const amount = toNumber(d.amount);
  const yieldValue = toNumber(d.yield) ?? (d.status === 'forecast' && price.value && amount ? (amount / price.value) * 100 : null);
  return {
    key: `${d.record_date}-${index}`,
    date: shortDay(d.record_date),
    payment: d.payment_date ? t('instrument.payment', { date: shortDay(d.payment_date) }) : '',
    status: t(`instrument.dividendStatus.${d.status}`),
    statusClass: STATUS_CLASS[d.status] ?? STATUS_CLASS.paid,
    amount: d.amount ? formatMoney(d.amount, d.currency) : '—',
    amountClass: AMOUNT_CLASS[d.status] ?? 'text-slate-100',
    yield: yieldValue === null ? '' : `${d.status === 'forecast' ? '≈ ' : ''}${formatNumber(yieldValue, 2)} %`,
  };
}));

// «Операции»: the user's operations with this instrument in every account (decision 16), as on the
// operations screen — by day, newest first, an account name on each row.
const operations = ref([]);
const operationsCursor = ref(null);
const operationsLoading = ref(false);
const operationsLoaded = ref(false);
const operationsError = ref('');
const expandedOperation = ref(null);
const operationDays = computed(() => groupByDay(operations.value));

// Category filter as on the operations screen (decision 17): chips of the categories this instrument has,
// several at once; with a choice — the count and the totals by currency.
const opCategories = ref([]);
const opCounts = ref({});
const opTotal = ref(null);
const opVisibleCategories = computed(() => CATEGORIES.filter((c) => opCounts.value[c] || opCategories.value.includes(c)));
const opStatus = computed(() => {
  if (opCategories.value.length === 0 || !opTotal.value) {
    return null;
  }
  const names = CATEGORIES.filter((c) => opCategories.value.includes(c)).map((c) => t(`operations.cat.${c}`)).join(', ');
  const sums = new Map(opTotal.value.sums.map((s) => [s.currency, s.amount]));
  return { text: `${names} · ${plural(opTotal.value.count, 'operations.count')}`, sums: sums.size > 0 ? formatCurrencySums(sums) : '' };
});

function toggleOpCategory(category) {
  const selected = opCategories.value;
  opCategories.value = selected.includes(category) ? selected.filter((c) => c !== category) : [...selected, category];
}

watch(opCategories, () => {
  expandedOperation.value = null;
  loadOperations();
});

let operationsRequest = 0;

async function loadOperations(more = false) {
  if (more && (operationsLoading.value || !operationsCursor.value)) {
    return;
  }
  const id = more ? operationsRequest : ++operationsRequest;
  operationsLoading.value = true;
  operationsError.value = '';
  try {
    const { data } = await axios.get('/operations/list', {
      params: {
        instrument_uid: props.uid,
        ...(opCategories.value.length > 0 ? { categories: opCategories.value } : {}),
        ...(more ? { cursor: operationsCursor.value } : {}),
      },
    });
    if (id !== operationsRequest) {
      return;
    }
    operations.value = more ? [...operations.value, ...data.items] : data.items;
    operationsCursor.value = data.next_cursor;
    if (!more) {
      opCounts.value = data.counts ?? {};
      opTotal.value = data.total ?? null;
    }
    operationsLoaded.value = true;
  } catch (e) {
    if (e?.response?.status !== 401) {
      operationsError.value = extractApiError(e, t('instrument.operationsFailed'));
    }
  } finally {
    operationsLoading.value = false;
  }
}

watch(shownTab, (value) => {
  if (value === 'operations' && !operationsLoaded.value) {
    loadOperations();
  }
});

// Next page of the open list (news or operations) before its end is reached.
const sentinel = ref(null);
let observer = null;
watch(sentinel, () => nextTick(() => {
  observer?.disconnect();
  if (sentinel.value && 'IntersectionObserver' in window) {
    observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        if (shownTab.value === 'operations') {
          loadOperations(true);
        } else {
          loadNews(true);
        }
      }
    }, { rootMargin: '600px' });
    observer.observe(sentinel.value);
  }
}));

function reloadAll() {
  loadCard();
  loadCandles();
  if (newsLoaded.value) {
    loadNews();
  }
  if (dividendsLoaded.value) {
    loadDividends();
  }
  if (operationsLoaded.value) {
    loadOperations();
  }
}
watch(() => props.dataVersion, reloadAll);

function onVisibility() {
  if (document.visibilityState === 'visible') {
    loadCard();
  }
}

onMounted(() => {
  loadCard();
  loadCandles();
  pollTimer = window.setInterval(() => {
    if (document.visibilityState === 'visible') {
      loadCard();
    }
  }, POLL_MS);
  document.addEventListener('visibilitychange', onVisibility);
});
onUnmounted(() => {
  window.clearInterval(pollTimer);
  document.removeEventListener('visibilitychange', onVisibility);
  observer?.disconnect();
});

const SEGMENT = 'h-8 flex-1 rounded-full text-[0.875rem] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400';
// Tabs: on a phone as wide as their text (the bar scrolls), from sm equal like the segments.
const TAB = 'h-8 shrink-0 grow rounded-full px-3.5 text-[0.875rem] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 sm:basis-0 sm:px-0';
</script>

<template>
  <!-- One column; from lg the «У вас» blocks stand in a column on the right (decision 7) -->
  <div
    class="grid grid-cols-[minmax(0,1fr)] items-start gap-3 sm:gap-4 lg:gap-6"
    :class="holdings.length > 0 ? 'lg:grid-cols-[minmax(0,1fr)_340px] lg:grid-rows-[auto_1fr]' : ''"
  >
    <div
      v-if="loading"
      :class="PANEL"
      aria-busy="true"
    >
      <div class="h-10 w-2/3 rounded bg-slate-800 motion-safe:animate-pulse" />
      <div class="mt-4 h-8 w-1/3 rounded bg-slate-800 motion-safe:animate-pulse" />
    </div>
    <div
      v-else-if="error && !card"
      :class="[PANEL, 'text-[0.9375rem] text-rose-300']"
    >
      {{ error }}
    </div>

    <template v-else-if="card">
      <!-- Header: logo, name, live price, day change -->
      <section :class="[PANEL, 'lg:col-start-1']">
        <div class="flex items-center gap-3">
          <img
            v-if="card.logo_url"
            :src="card.logo_url"
            alt=""
            class="size-11 shrink-0 rounded-full bg-slate-800 object-cover"
          >
          <span
            v-else
            class="flex size-11 shrink-0 items-center justify-center rounded-full bg-slate-700 text-[0.9375rem] font-semibold text-white"
            :style="card.logo_color ? { backgroundColor: card.logo_color } : null"
            aria-hidden="true"
          >{{ name.trim().charAt(0).toUpperCase() }}</span>
          <div class="min-w-0">
            <h1 class="truncate text-[1.0625rem] font-semibold text-slate-100 sm:text-lg">
              {{ name }}
            </h1>
            <p
              v-if="subtitle"
              class="truncate text-[0.8125rem] text-slate-500"
            >
              {{ subtitle }}
            </p>
          </div>
        </div>
        <p class="mt-4 text-[1.75rem] font-semibold leading-8 tabular-nums text-slate-100">
          {{ formatPrice(card.price) }}
        </p>
        <p
          v-if="changeText"
          class="mt-1 text-[0.9375rem] tabular-nums sm:text-sm"
          :class="signClass(Number(change.toFixed(2)))"
        >
          {{ changeText }} <span class="text-slate-500">{{ t('instrument.today') }}</span>
        </p>
      </section>

      <!-- «У вас» on a wide screen: a column on the right (decision 7); narrower — under the chart (decision 18) -->
      <InstrumentHoldings
        v-if="holdings.length > 0"
        class="hidden lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:block"
        :holdings="holdings"
      />

      <!-- Left column on a wide screen: tabs and what they show -->
      <div class="space-y-3 sm:space-y-4 lg:col-start-1">
        <!-- Tabs «График | Дивиденды | Новости» (decisions 3, 9) -->
        <!-- On a phone the tabs that do not fit scroll inside the bar, not the page (docs/tpay/SPEC.md, decision 5) -->
        <div
          class="flex overflow-x-auto rounded-full bg-slate-800 p-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          role="tablist"
        >
          <button
            v-for="key in tabs"
            :key="key"
            type="button"
            role="tab"
            :aria-selected="shownTab === key"
            :class="[TAB, shownTab === key ? 'bg-slate-100 text-slate-950' : 'text-slate-300']"
            @click="tab = key"
          >
            {{ t(`instrument.tab.${key}`) }}
          </button>
        </div>

        <template v-if="shownTab === 'chart'">
          <section :class="PANEL">
            <div class="flex items-baseline justify-between gap-3">
              <p
                class="text-[0.9375rem] tabular-nums sm:text-sm"
                :class="periodChange === null ? 'text-slate-500' : signClass(Number(periodChange.toFixed(2)))"
              >
                {{ periodChange === null ? '—' : formatSignedPercent(periodChange, 2) }}
                <span class="text-slate-500">{{ t(`instrument.periodLabel.${period}`) }}</span>
              </p>
            </div>
            <div class="mt-3">
              <div
                v-if="candlesLoading && candles.length === 0"
                class="rounded bg-slate-800 motion-safe:animate-pulse"
                :style="{ height: `${desktop ? 280 : 220}px` }"
              />
              <p
                v-else-if="candlesError"
                class="py-10 text-center text-[0.9375rem] text-rose-300"
              >
                {{ candlesError }}
              </p>
              <p
                v-else-if="points.length === 0"
                class="py-10 text-center text-[0.9375rem] text-slate-500"
              >
                {{ t('instrument.noChart') }}
              </p>
              <InstrumentChart
                v-else
                :class="candlesLoading ? 'opacity-50' : ''"
                :points="points"
                :period="period"
                :up="(periodChange ?? 0) >= 0"
                :format-price="formatPrice"
                :height="desktop ? 280 : 220"
                :markers="markers"
              />
            </div>
            <div
              class="mt-3 flex rounded-full bg-slate-800 p-0.5"
              role="radiogroup"
              :aria-label="t('instrument.periodAria')"
            >
              <button
                v-for="key in PERIODS"
                :key="key"
                type="button"
                role="radio"
                :aria-checked="period === key"
                :class="[SEGMENT, period === key ? 'bg-slate-100 text-slate-950' : 'text-slate-300']"
                @click="period = key"
              >
                {{ t(`instrument.period.${key}`) }}
              </button>
            </div>
          </section>

          <!-- «У вас» under the chart up to lg, on the chart tab only (decision 18) -->
          <InstrumentHoldings
            v-if="holdings.length > 0"
            class="lg:hidden"
            :holdings="holdings"
          />

          <!-- «Торги»: turnover, capitalization, index weight in a block of their own (decision 12) -->
          <section
            v-if="facts.length > 0"
            :class="PANEL"
          >
            <h2 class="text-[0.9375rem] font-semibold text-slate-100">
              {{ t('instrument.trading') }}
            </h2>
            <dl class="mt-2 divide-y divide-slate-800 text-[0.9375rem] sm:text-sm">
              <div
                v-for="fact in facts"
                :key="fact.key"
                class="flex min-h-11 items-center justify-between gap-3"
              >
                <dt class="text-slate-400">
                  {{ fact.label }}
                </dt>
                <dd class="tabular-nums text-slate-100">
                  {{ fact.value }}
                </dd>
              </div>
            </dl>
          </section>

          <!-- «Показатели»: fundamentals of a share by group, rows without data hidden (decision 13) -->
          <section
            v-if="fundamentalGroups.length > 0"
            :class="PANEL"
          >
            <h2 class="text-[0.9375rem] font-semibold text-slate-100">
              {{ t('instrument.fundamentals') }}
            </h2>
            <div
              v-for="group in fundamentalGroups"
              :key="group.key"
              class="mt-3"
            >
              <h3 class="text-[0.8125rem] text-slate-500">
                {{ group.title }}
              </h3>
              <dl class="divide-y divide-slate-800 text-[0.9375rem] sm:text-sm">
                <div
                  v-for="row in group.rows"
                  :key="row.key"
                  class="flex min-h-11 items-center justify-between gap-3"
                >
                  <dt class="text-slate-400">
                    {{ row.label }}
                  </dt>
                  <dd
                    class="tabular-nums"
                    :class="row.negative ? 'text-rose-400' : 'text-slate-100'"
                  >
                    {{ row.value }}
                  </dd>
                </div>
              </dl>
            </div>
            <p class="mt-3 text-[0.75rem] text-slate-500">
              {{ t('instrument.fundamentalsSource') }}
            </p>
          </section>
        </template>

        <!-- Dividends, newest first (decision 9) -->
        <section
          v-else-if="shownTab === 'dividends'"
          :class="PANEL"
        >
          <p
            v-if="dividendsError"
            class="text-[0.9375rem] text-rose-300"
          >
            {{ dividendsError }}
          </p>
          <div
            v-else-if="!dividendsLoaded"
            class="space-y-2"
            aria-busy="true"
          >
            <div class="h-11 rounded bg-slate-800 motion-safe:animate-pulse" />
            <div class="h-11 rounded bg-slate-800 motion-safe:animate-pulse" />
          </div>
          <p
            v-else-if="dividendRows.length === 0"
            class="py-6 text-center text-[0.9375rem] text-slate-500"
          >
            {{ t('instrument.noDividends') }}
          </p>
          <ul
            v-else
            class="-my-1 divide-y divide-slate-800"
          >
            <li
              v-for="row in dividendRows"
              :key="row.key"
              class="flex min-h-14 items-center justify-between gap-3 py-2"
            >
              <span class="min-w-0">
                <span class="flex items-center gap-2">
                  <span class="text-[0.9375rem] tabular-nums text-slate-100 sm:text-sm">{{ row.date }}</span>
                  <span
                    class="rounded-full px-2 py-0.5 text-[0.75rem]"
                    :class="row.statusClass"
                  >{{ row.status }}</span>
                </span>
                <span
                  v-if="row.payment"
                  class="block text-[0.8125rem] text-slate-500"
                >{{ row.payment }}</span>
              </span>
              <span class="shrink-0 text-right tabular-nums">
                <span
                  class="block text-[0.9375rem] sm:text-sm"
                  :class="row.amountClass"
                >{{ row.amount }}</span>
                <span
                  v-if="row.yield"
                  class="block text-[0.8125rem] text-slate-500"
                >{{ row.yield }}</span>
              </span>
            </li>
          </ul>
        </section>

        <!-- TPAY payout calculation (docs/tpay/SPEC.md) -->
        <InstrumentPayoutCalc
          v-else-if="shownTab === 'calc'"
          :uid="uid"
        />

        <!-- Operations with this instrument in every account (decision 16) -->
        <div
          v-else-if="shownTab === 'operations'"
          class="space-y-3 sm:space-y-4"
        >
          <!-- Category filter (decision 17) -->
          <div
            v-if="opVisibleCategories.length > 0"
          >
            <div class="-mx-4 flex gap-2 overflow-x-auto px-4 py-1 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:px-0">
              <button
                type="button"
                :class="chipClass(opCategories.length === 0)"
                :aria-pressed="opCategories.length === 0"
                @click="opCategories = []"
              >
                {{ t('operations.all') }}
              </button>
              <button
                v-for="category in opVisibleCategories"
                :key="category"
                type="button"
                :class="[chipClass(opCategories.includes(category)), 'flex items-center gap-1.5']"
                :aria-pressed="opCategories.includes(category)"
                @click="toggleOpCategory(category)"
              >
                {{ t(`operations.cat.${category}`) }}
                <span
                  v-if="desktop && opCounts[category]"
                  class="tabular-nums"
                  :class="opCategories.includes(category) ? 'text-slate-600' : 'text-slate-500'"
                >{{ opCounts[category] }}</span>
              </button>
            </div>
            <div
              v-if="opStatus"
              class="mt-1 flex min-h-8 items-center justify-between gap-3 text-[0.8125rem] sm:text-sm"
            >
              <span class="min-w-0 text-slate-300">
                <span class="block truncate">{{ opStatus.text }}</span>
                <span
                  v-if="opStatus.sums"
                  class="block truncate tabular-nums text-slate-100"
                >{{ opStatus.sums }}</span>
              </span>
              <button
                type="button"
                class="relative shrink-0 font-medium text-slate-100 before:absolute before:-inset-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
                @click="opCategories = []"
              >
                {{ t('operations.reset') }}
              </button>
            </div>
          </div>
          <div
            v-if="!operationsLoaded && !operationsError"
            :class="[PANEL, 'space-y-2']"
            aria-busy="true"
          >
            <div class="h-12 rounded bg-slate-800 motion-safe:animate-pulse" />
            <div class="h-12 rounded bg-slate-800 motion-safe:animate-pulse" />
          </div>
          <p
            v-else-if="operationsError && operations.length === 0"
            :class="[PANEL, 'text-[0.9375rem] text-rose-300']"
          >
            {{ operationsError }}
          </p>
          <p
            v-else-if="operations.length === 0"
            :class="[PANEL, 'text-center text-[0.9375rem] text-slate-500']"
          >
            {{ t('instrument.noOperations') }}
          </p>
          <template v-else>
            <section class="rounded-3xl bg-slate-900 py-2 sm:rounded-lg sm:border sm:border-slate-700 sm:bg-slate-900/40 sm:py-0">
              <div
                v-for="day in operationDays"
                :key="day.key"
              >
                <h2 class="px-5 pb-1 pt-3 text-[0.8125rem] text-slate-500 first-letter:uppercase sm:bg-slate-900/60 sm:py-1.5 sm:text-xs">
                  {{ dayLabel(day.key, { full: desktop }) }}
                </h2>
                <ul>
                  <li
                    v-for="op in day.items"
                    :key="op.id"
                  >
                    <OperationRow
                      :op="op"
                      :desktop="desktop"
                      :show-account="true"
                      :expanded="expandedOperation === op.id"
                      @toggle="expandedOperation = expandedOperation === op.id ? null : op.id"
                    />
                  </li>
                </ul>
              </div>
            </section>
            <div
              v-if="operationsCursor"
              ref="sentinel"
              class="text-center"
            >
              <button
                type="button"
                class="h-11 rounded-2xl px-4 text-[0.9375rem] font-medium text-slate-300 disabled:opacity-50 sm:h-9 sm:text-sm"
                :disabled="operationsLoading"
                @click="loadOperations(true)"
              >
                {{ operationsLoading ? t('common.loading') : t('news.loadMore') }}
              </button>
            </div>
          </template>
        </div>

        <div
          v-else
          class="space-y-3 sm:space-y-4"
        >
          <div
            v-if="newsLoading && news.length === 0"
            :class="[PANEL, 'space-y-2']"
            aria-busy="true"
          >
            <div class="h-16 rounded bg-slate-800 motion-safe:animate-pulse" />
            <div class="h-16 rounded bg-slate-800 motion-safe:animate-pulse" />
          </div>
          <p
            v-else-if="newsError && news.length === 0"
            :class="[PANEL, 'text-[0.9375rem] text-rose-300']"
          >
            {{ newsError }}
          </p>
          <p
            v-else-if="news.length === 0"
            :class="[PANEL, 'text-center text-[0.9375rem] text-slate-500']"
          >
            {{ t('instrument.noNews') }}
          </p>
          <template v-else>
            <NewsPostList
              :items="news"
              :desktop="desktop"
            />
            <div
              v-if="newsCursor"
              ref="sentinel"
              class="text-center"
            >
              <button
                type="button"
                class="h-11 rounded-2xl px-4 text-[0.9375rem] font-medium text-slate-300 disabled:opacity-50 sm:h-9 sm:text-sm"
                :disabled="newsLoading"
                @click="loadNews(true)"
              >
                {{ newsLoading ? t('common.loading') : t('news.loadMore') }}
              </button>
            </div>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>
